import {
  Divider,
  FormControl,
  FormLabel,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { AccountState, Provider } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { TerraformBashToggle } from '@/components/accounts/terraformBashToggle';
import { CustomAccountDisplayName } from '@/components/form/customAccountDisplayName';
import { FormTextField } from '@/components/form/formTextField';
import { getCloudFieldConfig } from '@/components/form/validation/cloudFieldValidation';
import { Loader } from '@/components/layout/loading';
import { AzureTenantSelect } from '@/components/selectors/azureTenantSelect';
import { CopyableCode } from '@/components/shared/copyableCode';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';

import type { AzureRestoreAccountState } from './azureConnectRestoreWizard';

import { StepErrorDialog } from '../../../../../../components/dialogs/stepErrorDialog';
import { BulletItem } from '../../bulletItem';
import { extractErrorMessage } from '../../errorHandling';
import { NumericBulletSection } from '../../numericBulletSection';

const subscriptionIdConfig = getCloudFieldConfig(
  Provider.Azure,
  'subscriptionId'
);
const resourceGroupNameConfig = getCloudFieldConfig(
  Provider.Azure,
  'resourceGroupName'
);

export const ConnectAzureRestoreAccount = (
  props: StepProps<AzureRestoreAccountState>
) => {
  const [isDeploying, setIsDeploying] = useState(false);
  const [customName, setCustomName] = useState<string>('');
  const dal = useDAL();

  const { data: backupApps, isLoading } = dal.cloudAccounts.azureApps.useApps(
    'Restore',
    props.wizardState.tenantId
  );

  const [error, setError] = useState<Error | null>(null);

  const resourceGroupName =
    props.wizardState.restoreConnectType === 'resourceGroup'
      ? (props.wizardState.restoreResourceGroupName ?? '')
      : '';

  const bashCode = `\
  # Login to the tenant
  az login --tenant "${props.wizardState.tenantId}"

  # Set the subscription
  az account set --subscription "${props.wizardState.restoreSubscriptionId}"

  # Get tokens in the correct context
  ARM_TOKEN=$(az account get-access-token --query accessToken -o tsv)
  GRAPH_TOKEN=$(az account get-access-token --resource https://graph.microsoft.com/ --query accessToken -o tsv)

  # Download and run restore script
  curl -fsSL https://eon.blob.core.windows.net/public/onboarding/v1.3.1/bash/restore.sh -o restore.sh && chmod +x restore.sh && \\
  AZURE_ACCESS_TOKEN="$ARM_TOKEN" \\
  AZURE_GRAPH_TOKEN="$GRAPH_TOKEN" \\
  ./restore.sh \\
    --subscription-id "${props.wizardState.restoreSubscriptionId}" \\
    --management-app-id "${backupApps?.managementAppId ?? ''}" \\
    --backup-app-id "${backupApps?.backupAppId ?? ''}"${
      backupApps?.adxClusterManagedIdentityPrincipalId
        ? ` \\\n    --adx-principal-id "${backupApps?.adxClusterManagedIdentityPrincipalId ?? ''}" \\`
        : ''
    }${
      props.wizardState.restoreConnectType === 'resourceGroup'
        ? `\n    --resource-group "${resourceGroupName ?? ''}"`
        : ''
    }`;

  const terraformCode = `\
  module "azure_restore" {
    source = "https://eon.blob.core.windows.net/public/onboarding/v1.3.1/terraform/restore.zip"
    subscription_id                  = "${props.wizardState.restoreSubscriptionId ?? ''}"
    management_app_id                = "${backupApps?.managementAppId ?? ''}"
    backup_restore_app_id            = "${backupApps?.backupAppId ?? ''}"${
      backupApps?.adxClusterManagedIdentityPrincipalId
        ? `\n    adx_service_principal_id         = "${backupApps?.adxClusterManagedIdentityPrincipalId ?? ''}"`
        : ''
    }${
      props.wizardState.restoreConnectType === 'resourceGroup'
        ? `\n    resource_group_name              = "${resourceGroupName ?? ''}"`
        : ''
    }
  }

  output "restore_onboarding" {
    value = module.azure_restore
  }`;

  const code =
    props.wizardState.restoreConnectMethod === 'terraform'
      ? terraformCode
      : bashCode;

  const onNextClick = async () => {
    setError(null);
    setIsDeploying(true);
    try {
      const { restoreAccount } = await dal.cloudAccounts.restore.connect.azure({
        tenantId: props.wizardState.tenantId,
        restoreSubscriptionId: props.wizardState.restoreSubscriptionId,
        restoreResourceGroupName: resourceGroupName || undefined,
        eonInternalResourceGroupName:
          props.wizardState.eonInternalResourceGroupName || undefined,
        name: customName || undefined,
      });

      if (
        restoreAccount.status &&
        restoreAccount.status !== AccountState.ACCOUNT_STATE_CONNECTED
      ) {
        console.log(restoreAccount);
        setError(new Error('Failed to deploy source account'));
      } else {
        setIsDeploying(false);
        await props.finish();
      }
    } catch (error) {
      const errorMessage = await extractErrorMessage(error);
      setError(new Error(errorMessage));
      setIsDeploying(false);
    }
  };

  const missingFirstStepFields =
    !props.wizardState.tenantId ||
    !props.wizardState.restoreSubscriptionId ||
    (props.wizardState.restoreConnectType === 'resourceGroup' &&
      !props.wizardState.restoreResourceGroupName);

  const missingSecondStepFields =
    !props.wizardState.eonInternalResourceGroupName;

  const isMissingFields = missingFirstStepFields || missingSecondStepFields;

  const validateForm = () => {
    const validationErrors =
      !subscriptionIdConfig.validation.validateInput(
        props.wizardState.restoreSubscriptionId
      ) ||
      (props.wizardState.restoreConnectType === 'resourceGroup' &&
        props.wizardState.restoreResourceGroupName &&
        !resourceGroupNameConfig.validation.validateInput(
          props.wizardState.restoreResourceGroupName
        )) ||
      (props.wizardState.eonInternalResourceGroupName &&
        !resourceGroupNameConfig.validation.validateInput(
          props.wizardState.eonInternalResourceGroupName
        ));

    if (validationErrors && isMissingFields) {
      return 'All fields are required, and some are in the wrong format. Fix the issues so you can proceed.';
    }

    if (validationErrors) {
      return 'Some fields are in the wrong format. Fix the issues so you can proceed.';
    }

    if (isMissingFields) {
      return 'All fields are required, but some are missing values. Fill in all fields so you can proceed.';
    }

    return '';
  };

  const nextButtonTooltip = validateForm();

  const bulletTitle = `Tell us which restore subscription you want to connect, and then
                  ${
                    props.wizardState.restoreConnectMethod === 'terraform'
                      ? 'apply the generated Terraform module'
                      : 'run the generated Bash command'
                  } in your environment.`;

  return (
    <>
      <StepContainer
        stepperIndex={3}
        hideStepper={true}
        stepperLabels={props.stepperLabels}
        canGoNext={() => {
          return !isDeploying && !nextButtonTooltip;
        }}
        nextButtonDisabledTooltip={nextButtonTooltip}
        nextButtonText={'Connect'}
        backButtonText='Cancel'
        onBackClick={props.back}
        sx={{ overflow: 'auto', paddingTop: '12px', paddingX: '0px' }}
        onNextClick={onNextClick}
      >
        <Stack divider={<Divider />} gap='24px' width='100%' height='auto'>
          <TerraformBashToggle
            text={`Before you start, make sure you can manage service principals in your Azure environment.`}
            connectMethod={props.wizardState.restoreConnectMethod}
            setConnectMethod={(connectMethod) => {
              props.setWizardState({
                ...props.wizardState,
                restoreConnectMethod: connectMethod,
              });
            }}
          />
          <Stack
            paddingX='min(100px,6vw)'
            paddingTop='16px'
            width='100%'
            height='auto'
          >
            {isLoading ? (
              <Loader />
            ) : (
              <Stack
                gap='40px'
                divider={<Divider sx={{ borderStyle: 'dashed' }} />}
              >
                <NumericBulletSection
                  bulletNumber={1}
                  title="Deploy Eon's restore subscription"
                >
                  <BulletItem
                    title={bulletTitle}
                    leftContent={
                      <>
                        <Stack
                          gap='24px'
                          divider={<Divider sx={{ borderStyle: 'dashed' }} />}
                        >
                          <Stack gap='24px'>
                            <AzureTenantSelect
                              tenantId={props.wizardState.tenantId}
                              onChange={(tenantId) =>
                                props.setWizardState({
                                  ...props.wizardState,
                                  tenantId,
                                })
                              }
                            />
                            <FormTextField
                              title='Subscription ID'
                              placeholder={subscriptionIdConfig.placeholder}
                              validation={subscriptionIdConfig.validation}
                              value={
                                props.wizardState.restoreSubscriptionId || ''
                              }
                              onChange={(event) =>
                                props.setWizardState({
                                  ...props.wizardState,
                                  restoreSubscriptionId: event.target.value,
                                })
                              }
                            />
                            <FormControl>
                              <FormLabel>
                                <Typography variant='subtitle1'>
                                  Allow Eon to restore to
                                </Typography>
                              </FormLabel>
                              <ToggleButtonGroup
                                sx={{ width: 'max-content' }}
                                exclusive
                                value={props.wizardState.restoreConnectType}
                                onChange={(_, value) => {
                                  props.setWizardState({
                                    ...props.wizardState,
                                    restoreConnectType: value,
                                  });
                                }}
                              >
                                <ToggleButton value='subscription'>
                                  All resource groups
                                </ToggleButton>
                                <ToggleButton value='resourceGroup'>
                                  Specific resource group
                                </ToggleButton>
                              </ToggleButtonGroup>
                            </FormControl>
                            {props.wizardState.restoreConnectType ===
                              'resourceGroup' && (
                              <FormTextField
                                title='Resource group name'
                                placeholder={
                                  resourceGroupNameConfig.placeholder
                                }
                                validation={resourceGroupNameConfig.validation}
                                value={
                                  props.wizardState.restoreResourceGroupName ||
                                  ''
                                }
                                onChange={(event) =>
                                  props.setWizardState({
                                    ...props.wizardState,
                                    restoreResourceGroupName:
                                      event.target.value,
                                  })
                                }
                              />
                            )}
                          </Stack>
                          <CustomAccountDisplayName
                            entityName='subscription'
                            customName={customName}
                            setCustomName={setCustomName}
                          />
                        </Stack>
                      </>
                    }
                    rightContent={
                      <CopyableCode
                        title={
                          props.wizardState.restoreConnectMethod === 'terraform'
                            ? 'main.tf'
                            : 'restore.sh'
                        }
                        code={code}
                        blur={missingFirstStepFields}
                      />
                    }
                  />
                </NumericBulletSection>
                <NumericBulletSection
                  bulletNumber={2}
                  title='Complete configuration'
                >
                  <BulletItem
                    title={`After running the ${
                      props.wizardState.restoreConnectMethod === 'terraform'
                        ? 'Terraform module'
                        : 'Bash command'
                    }, paste the output value below:`}
                    leftContent={
                      <FormTextField
                        title='eon_internal_resource_group_name'
                        placeholder='eon-restore-internal-rg'
                        validation={resourceGroupNameConfig.validation}
                        value={
                          props.wizardState.eonInternalResourceGroupName || ''
                        }
                        onChange={(event) =>
                          props.setWizardState({
                            ...props.wizardState,
                            eonInternalResourceGroupName: event.target.value,
                          })
                        }
                      />
                    }
                  />
                </NumericBulletSection>
              </Stack>
            )}
          </Stack>
        </Stack>
      </StepContainer>
      <StepErrorDialog
        isOpen={!!error}
        onClose={() => {
          setError(null);
          setIsDeploying(false);
        }}
        error={error}
      />
    </>
  );
};
